import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import useGeneralApiCall from 'components/Dashboard/apiCalls/useGeneralApiCall';
import { useCallContentRelated } from 'components/Dashboard/utilities/callContentRelated';

export const useFetchRegulationDocument = () => {
  const { generalApiCall } = useGeneralApiCall();
  const { isAuthenticated } = useAuth0();

  const fetchRegulationDocument = async ({ id, source } = {}) => {
    if (!id) return null;
    const document = await generalApiCall({
      pathname: `/api/regulation-document/${id}`,
      method: 'get',
      requestSource: source,
      needsAuthentication: isAuthenticated,
      notShowErrorMessage: true,
    });
    return document ?? null;
  };

  return { fetchRegulationDocument };
};

const useRegulationDocumentData = ({ commentsOnly = false } = {}) => {
  const { id } = useParams();
  const { isAuthenticated } = useAuth0();
  const { generalApiCall } = useGeneralApiCall();
  const { callContentRelated } = useCallContentRelated();
  const { fetchRegulationDocument } = useFetchRegulationDocument();
  const [regulationDocument, setRegulationDocument] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentsTotal, setCommentsTotal] = useState(0);
  const [isCommentsLoading, setIsCommentsLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(!commentsOnly);
  const [contentRelated, setContentRelated] = useState(null);
  const [isLoadingRelatedContent, setIsLoadingRelatedContent] = useState(true);

  const loadRegulationDocument = async ({ source } = {}) => {
    if (!id) {
      setRegulationDocument(null);
      setIsLoading(false);
      return null;
    }

    setIsLoading(true);
    const document = await fetchRegulationDocument({ id, source });
    if (!source?.signal?.aborted) {
      setRegulationDocument(document);
      setIsLoading(false);
    }

    return document;
  };

  const loadRegulationDocumentRelatedContent = async ({ source, document } = {}) => {
    const objectId = document?.objectId;
    const objectType = document?.objectType;
    if (!objectId || !objectType) {
      setContentRelated(null);
      setIsLoadingRelatedContent(false);
      return null;
    }
    setIsLoadingRelatedContent(true);
    try {
      const related = await callContentRelated({ objectId, objectType, source });
      if (!source?.signal?.aborted) {
        setContentRelated(related ?? null);
      }
      return related;
    } finally {
      if (!source?.signal?.aborted) {
        setIsLoadingRelatedContent(false);
      }
    }
  };

  const loadRegulationDocumentComments = async ({ source, pageNumber = 1, pageSize = 20 } = {}) => {
    const regulationDocumentId = Number(id);

    if (!regulationDocumentId) {
      setComments([]);
      setCommentsTotal(0);
      return null;
    }

    const isInitialLoad = pageNumber === 1;
    if (isInitialLoad) {
      setIsCommentsLoading(true);
    }
    try {
      const query = new URLSearchParams({
        pageNumber: String(pageNumber),
        pageSize: String(pageSize),
      });
      const results = await generalApiCall({
        pathname: `/api/regulation-document-comment/list-comments-for-document/${regulationDocumentId}?${query}`,
        method: 'get',
        requestSource: source,
        needsAuthentication: isAuthenticated,
        notShowErrorMessage: true,
      });
      if (!source?.signal?.aborted) {
        let hits = results?.results ?? [];
        const total = results?.totalCount;
        setComments(pageNumber > 1 ? (prev) => [...prev, ...hits] : hits);
        setCommentsTotal(total);
      }
      return results;
    } catch (error) {
      return null;
    } finally {
      if (isInitialLoad && !source?.signal?.aborted) {
        setIsCommentsLoading(false);
      }
    }
  };

  useEffect(() => {
    const source = new AbortController();
    if (!commentsOnly) {
      (async () => {
        const document = await loadRegulationDocument({ source });
        if (document) {
          loadRegulationDocumentRelatedContent({ source, document });
        } else {
          setContentRelated(null);
          setIsLoadingRelatedContent(false);
        }
      })();
    } else {
      setContentRelated(null);
      setIsLoadingRelatedContent(false);
    }
    loadRegulationDocumentComments({ source });

    return () => {
      source.abort('Regulation document request cancelled by the user.');
      if (!commentsOnly) {
        setContentRelated(null);
      }
      setComments([]);
      setCommentsTotal(0);
    };
  }, [id, isAuthenticated, commentsOnly]);

  return {
    comments,
    commentsTotal,
    id,
    isCommentsLoading,
    isLoading,
    loadRegulationDocumentComments,
    loadRegulationDocument,
    regulationDocument,
    contentRelated,
    isLoadingRelatedContent,
  };
};

export default useRegulationDocumentData;
